import type { Page as PageType } from 'src/api/types/page';
import { useEffect } from 'react';

import {privacyPolicyContent1,policyPoints,personalInfoList,dataDisclosurePoints,userRightsPoints, eeaRights ,securityMeasures} from './privacyPolicyContent'
import {
    Box,
    Button,
    Container,
    Typography,
    List,
    ListItem,
    ListItemText
 
} from '@mui/material';
import { motion } from 'framer-motion';

const PrivacyPolicy: PageType = () => {
    useEffect(() => {
        window.scrollTo(0, 0); // Scrolls to the top of the page when this component is mounted
      }, []);
    return (
        <Box>
        <Box
            component="section"
            sx={{
                backgroundColor: '#0F1521',
                color: 'common.white',
                position: 'relative',
                overflow: 'hidden',
                py: { xs: 6, md: 10 },
                minHeight: { xs: 'auto', md: '80vh' },
                display: 'flex',
                alignItems: 'center'
            }}
        >
            {/* Animated gradient background */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.1,
                    background: 'radial-gradient(circle at 50% 50%, #FF6B00 0%, transparent 50%)',
                    animation: 'pulse 8s ease-in-out infinite',
                    '@keyframes pulse': {
                        '0%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.5)' },
                        '100%': { transform: 'scale(1)' }
                    }
                }}
            />
            <motion.div
                initial={{ width: 0 }}
                animate={{ width: '100px' }}
                transition={{ duration: 1, delay: 0.5 }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: '20%',
                        left: '20%',
                        height: '2px',
                        backgroundColor: '#FF6B00',
                        opacity: 0.3,
                        zIndex: 0
                    }}
                />
            </motion.div>

            {/* Globe image with parallax effect */}
            <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 0.8, scale: 1 }}
                transition={{ duration: 1.5 }}
                style={{
                    position: 'absolute',
                    bottom: '-15%',
                    right: '0%',
                    width: '70%',
                    maxWidth: '1000px',
                    zIndex: 0,
                }}
            >
                <Box
                    component="img"
                    src="/globe.avif"
                    alt="Globe"
                    sx={{
                        width: '100%',
                        height: 'auto',
                        filter: 'hue-rotate(30deg) saturate(0.8)',
                        animation: 'float 6s ease-in-out infinite',
                        '@keyframes float': {
                            '0%, 100%': { transform: 'translateY(0px)' },
                            '50%': { transform: 'translateY(-20px)' }
                        }
                    }}
                />
            </motion.div>

            <Container
                maxWidth="lg"
                sx={{
                    position: 'relative',
                    zIndex: 2,
                    mt: { xs: 0, md: -10 }
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        maxWidth: '700px',
                        mx: 'auto',
                        textAlign: 'center'
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <Typography
                            variant="h2"
                            sx={{
                                color: 'common.white',
                                fontWeight: 700,
                                mb: { xs: 3, md: 4 },
                                fontSize: { xs: '2.5rem', sm: '3rem', md: '4.25rem' },
                                lineHeight: 2.3,
                                textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}
                        >
                          Privacy &{' '}
                            <Typography
                                component="span"
                                variant="inherit"
                                sx={{
                                    background: 'linear-gradient(90deg, #FF6B00 0%, #FF9D00 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    fontWeight: 700
                                }}
                            >
                                Policy
                            </Typography>
                        </Typography>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                    >
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                        whileHover={{ scale: 1.05 }}
                    >
                        <Button
                            component="a"
                            href="https://cal.com/support-team-qsszsx"
                            target="_blank"
                            variant="contained"
                            size="large"
                            sx={{
                                py: 1.25,
                                px: 4,
                                borderRadius: 2,
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                backgroundImage: 'linear-gradient(to right, #360033 0%, #0b8793 51%, #360033 100%)',
                                backgroundSize: '200% auto',
                                color: 'white',
                                boxShadow: '0 0 20px #eee',
                                textTransform: 'uppercase',
                                transition: '0.5s',
                                padding: '15px 35px',
                                height: '46px',
                                width: 'fit-content',
                                margin: '10px',
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                marginBottom: '20px',
                                gap: '5px',
                                '&:hover': {
                                    backgroundPosition: 'right center',
                                    color: '#fff',
                                    textDecoration: 'none',
                                },
                            }}
                        >
                            Talk to us
                        </Button>
                    </motion.div>
                </Box>
            </Container>


        </Box>
        <Box 
         sx={{
            backgroundColor: 'white',
            color: 'black',
            display: 'flex',
            flexDirection: 'column',
            px: {
              xs: 2.5,  // 20px (1 spacing unit = 8px)
              md: 3.75, // 30px
              lg: 8.25  // 50px
            },
            py: {
                xs: 2.5,  // 20px (1 spacing unit = 8px)
                md: 3.75, // 30px
                lg: 4.25  // 50px
              }
            
         
        }}>
    
         <Typography
  sx={{
    textDecoration: 'underline',
    textDecorationColor: 'black', 
    fontFamily: 'Satoshi, sans-serif',
    color: '#FF6B00',
    fontWeight: 700,
    mt:2,
    fontSize: {
      xs: '18px',
      sm: '20px',
      md: '24px',
    },
    lineHeight: 2,
  }}
>
    PRIVACY POLICY
        </Typography>
        <Typography
  sx={{
    textDecoration: 'underline',
    textDecorationColor: 'black', 
    fontFamily: 'Satoshi, sans-serif',
    color: '#FF6B00',
    fontWeight: 700,
    mt:2,
    fontSize: {
      xs: '16px',
      sm: '18px',
      md: '22px',
    },
    lineHeight: 1.5,
  }}
>
1. Introduction
        </Typography>
        {privacyPolicyContent1.map((text, index) => (
        <Typography
          key={index}
          sx={{
            fontFamily: 'Satoshi, sans-serif',
            fontSize: {
              xs: '16px',
              sm: '18px',
              md: '20px',
            },
            lineHeight: 1.5,
            mt: 3,
          }}
        >
          {text}
        </Typography>
      ))}   
        
            {policyPoints.map((section, index) => (
            <Typography
                key={index}
                sx={{
                fontFamily: 'Satoshi, sans-serif',
                fontWeight: 400,
                textTransform: 'lowercase',
                fontSize: {
                    xs: '16px',
                    sm: '18px',
                    md: '20px',
                },
                lineHeight: 1.7,
                color: 'black',
                mt: 2,
                textAlign: 'justify',
                }}
            >
                <span
                style={{
                    color: '#FF6B00',
                    textDecoration: 'underline',
                    textDecorationColor: 'black',
                    textTransform: 'lowercase',
                    fontWeight: 700,
                }}
                >
                {section.title}
                </span>{' '}
                {section.content}
            </Typography>
            ))}
     
            <Typography
        sx={{
            textDecoration: 'underline',
            textDecorationColor: 'black', 
            fontFamily: 'Satoshi, sans-serif',
            color: '#FF6B00',
            fontWeight: 700,
            mt:2,
            fontSize: {
            xs: '16px',
            sm: '18px',
            md: '20px',
            },
            lineHeight: 2,
        }}
        >
        2 Purpose
            </Typography>

            <Typography  
            sx={{
                fontFamily: 'Satoshi, sans-serif',
                fontSize: {
                xs: '16px',
                sm: '18px',
                md: '20px',
                },
                lineHeight: 1.5,
                mt: 3,
            }}
            >
                This privacy policy explains what you can expect from PlatinX Exchange on how we protect your information as well as legally binding issues when you use or access PlatinX Exchange and on how you use them – through portal, website, mobile app etc.
            </Typography>
            
         <Typography
  sx={{
    textDecoration: 'underline',
    textDecorationColor: 'black', 
    fontFamily: 'Satoshi, sans-serif',
    color: '#FF6B00',
    fontWeight: 700,
    mt:2,
    fontSize: {
      xs: '16px',
      sm: '28px',
      md: '20px',
    },
    lineHeight: 2,
  }}
>
3 Collection of Personal Information
        </Typography>
        <Typography
               
                sx={{
                fontFamily: 'Satoshi, sans-serif',
                fontWeight: 400,
                textTransform: 'lowercase',
                fontSize: {
                    xs: '16px',
                    sm: '18px',
                    md: '20px',
                },
                lineHeight: 1.7,
                color: 'black',
                mt: 2,
                textAlign: 'justify',
                }}
            >
                <span
                style={{
                    color: '#FF6B00',
                    textDecoration: 'underline',
                    textDecorationColor: 'black',
                    textTransform: 'lowercase',
                    fontWeight: 700,
                }}
                >
               3.1
                </span>{' '}
                To provide the best services, we may collect and use the following information at our discretion
        </Typography>
            <List sx={{ listStyleType: 'disc', pl: 4 }}>
            {personalInfoList.map((item, index) => (
                <ListItem
                key={index}
                sx={{
                    display: 'list-item',
                    color: '#FF6B00',
                    mt:1,
                }}
                >
                <Typography
                    sx={{
                    fontFamily: 'Satoshi, sans-serif',
                    color: 'black',
                    fontSize: {
                        xs: '16px',
                        sm: '18px',
                        md: '20px',
                    },
                    lineHeight: 1.,
                    }}
                >
                    {item}
                </Typography>
                </ListItem>
            ))}
            </List>
            <Typography
                    
                    sx={{
                    fontFamily: 'Satoshi, sans-serif',
                    fontWeight: 400,
                    textTransform: 'lowercase',
                    fontSize: {
                        xs: '16px',
                        sm: '18px',
                        md: '20px',
                    },
                    lineHeight: 1.7,
                    color: 'black',
                    mt: 2,
                    textAlign: 'justify',
                    }}
                >
                    <span
                    style={{
                        color: '#FF6B00',
                        textDecoration: 'underline',
                        textDecorationColor: 'black',
                        textTransform: 'lowercase',
                        fontWeight: 700,
                    }}
                    >
                    3.2
                    </span>{' '}
                    PlatinX Exchange may also collect, receive, and use the following Personal Information from you to monitor and improve the function of PlatinX Exchange website, enhancing your experience in using our services
            </Typography>
                <Typography
                            sx={{
                                fontFamily: 'Satoshi, sans-serif',
                                fontSize: {
                                xs: '16px',  // small screens
                                sm: '18px',
                                md: '20px',  // medium and up
                                },
                                lineHeight: 1.5,
                                mt:1
                                
                            }}
                
                >
            Location Information – Information that is automatically collected via analytics systems providers to determine your location, including your IP address and/or domain name and any external page that referred you to us, your login information, browser type and version, time zone setting, browser plug-in types and versions, operating system, and platform
            </Typography>
            <Typography
                            sx={{
                                fontFamily: 'Satoshi, sans-serif',
                                fontSize: {
                                xs: '16px',  // small screens
                                sm: '18px',
                                md: '20px',  // medium and up
                                },
                                lineHeight: 1.5,
                                mt:1
                                
                            }}
                
                >
                    Log Information – Information that is generated by your use of PlatinX Exchange services that is automatically collected and stored in our server logs. This may include, but is not limited to, device-specific information, location information, system activity and any internal and external information related to pages that you visit, including the full Uniform Resource Locators (URL) clickstream to, through and from our Website or App (including date and time; page response times, download errors, length of visits to certain pages, page interaction information (such as scrolling, clicks, and mouse-overs), and methods used to browse away from the page;

            </Typography>
            <Typography
                            sx={{
                                fontFamily: 'Satoshi, sans-serif',
                                fontSize: {
                                xs: '16px',  // small screens
                                sm: '18px',
                                md: '20px',  // medium and up
                                },
                                lineHeight: 1.5,
                                mt:1
                                
                            }}
                
                >
            User Account Information – Information that is generated by your PlatinX Exchange account activities including, but not limited to, instructions regarding funding and disbursement, orders, trades, account balances, and other transactional information;
            </Typography>
            <Typography
                            sx={{
                                fontFamily: 'Satoshi, sans-serif',
                                fontSize: {
                                xs: '16px',  // small screens
                                sm: '18px',
                                md: '20px',  // medium and up
                                },
                                lineHeight: 1.5,
                                mt:1
                                
                            }}
                
                >
            Correspondence – Information that you provide to us in written or oral correspondence, including opening a PlatinX Exchange User Account, engaging customer support, and initiating other kinds of communications to contact us;
            </Typography>
            <Typography
                sx={{
                    fontFamily: 'Satoshi, sans-serif',
                    fontSize: {
                    xs: '16px',  // small screens
                    sm: '18px',
                    md: '20px',  // medium and up
                    },
                    lineHeight: 1.5,
                    mt:1
                    
                }}
      
      >
 Other information that we may receive from other third parties, including but not limited to banks or financial institutions that you use for transaction purposes. We may process information included in your personal profile, which may include your location, time zone and website (Profile Information). The Profile Information may be processed for the purposes of providing you a better user experience when using our Services. The legal basis for this processing is your consent.
          </Typography>
          <Typography
                sx={{
                fontFamily: 'Satoshi, sans-serif',
                fontWeight: 400,
                textTransform: 'lowercase',
                fontSize: {
                    xs: '16px',
                    sm: '18px',
                    md: '20px',
                },
                lineHeight: 1.7,
                color: 'black',
                mt: 2,
                textAlign: 'justify',
                }}
            >
                <span
                style={{
                    color: '#FF6B00',
                    textDecoration: 'underline',
                    textDecorationColor: 'black',
                    textTransform: 'lowercase',
                    fontWeight: 700,
                }}
                >
               3.3 
                </span>{' '}
                PlatinX Exchange reserves the right to use the information you provided while using our services even if the registration is left incomplete or abandoned.
            </Typography>
            <Typography
                sx={{
                fontFamily: 'Satoshi, sans-serif',
                fontWeight: 400,
                textTransform: 'lowercase',
                fontSize: {
                    xs: '16px',
                    sm: '18px',
                    md: '20px',
                },
                lineHeight: 1.7,
                color: 'black',
                mt: 2,
                textAlign: 'justify',
                }}
            >
                <span
                style={{
                    color: '#FF6B00',
                    textDecoration: 'underline',
                    textDecorationColor: 'black',
                    textTransform: 'lowercase',
                    fontWeight: 700,
                }}
                >
               3.4
                </span>{' '}
                If the user is using personal information of some third person, spouse/sibling/son/daughter for using our services then you agree that you have obtained consent from that person to disclose his/her personal information as well as consent to collect, use and disclosure of his/her information under this Privacy Policy.
            </Typography>
            <Typography
  sx={{
    textDecoration: 'underline',
    textDecorationColor: 'black', 
    fontFamily: 'Satoshi, sans-serif',
    color: '#FF6B00',
    fontWeight: 700,
    mt:2,
    fontSize: {
      xs: '16px',
      sm: '18px',
      md: '22px',
    },
    lineHeight: 1.5,
  }}
>
4. Use of Personal Information

            </Typography>
            {dataDisclosurePoints.map((section, index) => (
  <Typography
    key={index}
    sx={{
      fontFamily: 'Satoshi, sans-serif',
      fontWeight: 400,
      textTransform: 'lowercase',
      fontSize: {
        xs: '16px',
        sm: '18px',
        md: '20px',
      },
      lineHeight: 1.7,
      color: 'black',
      mt: 2,
      textAlign: 'justify',
    }}
  >
    <span
      style={{
        color: '#FF6B00',
        textDecoration: 'underline',
        textDecorationColor: 'black',
        textTransform: 'lowercase',
        fontWeight: 700,
      }}
    >
      {section.title}
    </span>{' '}
    {section.content}
  </Typography>
         ))}


<Typography
  sx={{
    textDecoration: 'underline',
    textDecorationColor: 'black', 
    fontFamily: 'Satoshi, sans-serif',
    color: '#FF6B00',
    fontWeight: 700,
    mt:2,
    fontSize: {
      xs: '16px',
      sm: '18px',
      md: '20px',
    },
    lineHeight: 1.5,
  }}
>
5.    Personal Data Rights
        </Typography>
    
{userRightsPoints.map((section, index) => (
  <Typography
    key={index}
    sx={{
      fontFamily: 'Satoshi, sans-serif',
      fontWeight: 400,
      fontSize: {
        xs: '16px',
        sm: '18px',
        md: '20px',
      },
      lineHeight: 1.7,
      color: 'black',
      mt: 3,
      textAlign: 'justify',
    }}
  >
    <span
      style={{
        color: '#FF6B00',
        textDecoration: 'underline',
        textDecorationColor: 'black',
        fontWeight: 700,
      }}
    >
      {section.title}
    </span>{' '}
    {section.content}
  </Typography>
))}

{/** EEA Rights List (only shown after 5.2) */}
<List sx={{ mt: 2 }}>
  {eeaRights.map((right, index) => {
    const alphabet = String.fromCharCode(97 + index); // 97 = 'a'
    return (
      <ListItem key={index} sx={{ py: 0 }}>
        <ListItemText
          primary={`(${alphabet}) ${right}`}
          primaryTypographyProps={{
            fontFamily: 'Satoshi, sans-serif',
            fontSize: {
              xs: '16px',
              sm: '18px',
              md: '20px',
            },
            color: 'black',
            lineHeight: 1.5,
          }}
        />
      </ListItem>
    );
  })}
</List>
            <Typography
            sx={{
                textDecoration: 'underline',
                textDecorationColor: 'black', 
                fontFamily: 'Satoshi, sans-serif',
                color: '#FF6B00',
                fontWeight: 700,
                mt:2,
                fontSize: {
                xs: '16px',
                sm: '18px',
                md: '20px',
                },
                lineHeight: 1.5,
            }}
            >
            6. Protection of Personal Information
                    </Typography>
{securityMeasures.map((section, index) => (
  <div key={index}>
    <Typography
      sx={{
        fontFamily: 'Satoshi, sans-serif',
        fontWeight: 400,
        fontSize: {
          xs: '16px',
          sm: '18px',
          md: '20px',
        },
        lineHeight: 1.7,
        color: 'black',
        mt: 3,
        textAlign: 'justify',
      }}
    >
      <span
        style={{
          color: '#FF6B00',
          textDecoration: 'underline',
          textDecorationColor: 'black',
          fontWeight: 700,
        }}
      >
        {section.title}
      </span>{' '}
      {section.content}
    </Typography>

    {section.points.length > 0 && (
      <List sx={{ pl: 2, mt: 1 }}>
        {section.points.map((point, idx) => (
          <ListItem key={idx} sx={{ py: 0 }}>
            <ListItemText
              primary={point}
              primaryTypographyProps={{
                fontFamily: 'Satoshi, sans-serif',
                fontSize: {
                  xs: '16px',
                  sm: '18px',
                  md: '20px',
                },
                color: 'black',
                lineHeight: 1.5,
              }}
            />
          </ListItem>
        ))}
      </List>
    )}
  </div>
))}

                  <Typography
            sx={{
                textDecoration: 'underline',
                textDecorationColor: 'black', 
                fontFamily: 'Satoshi, sans-serif',
                color: '#FF6B00',
                fontWeight: 700,
                mt:2,
                fontSize: {
                xs: '16px',
                sm: '18px',
                md: '20px',
                },
                lineHeight: 1.5,
            }}
            >
          7 Data Retention
                    </Typography>
                    <Typography
                sx={{
                    fontFamily: 'Satoshi, sans-serif',
                    fontSize: {
                    xs: '16px',  // small screens
                    sm: '18px',
                    md: '20px',  // medium and up
                    },
                    lineHeight: 1.5,
                    mt:1
                    
                }}
      
                    >
                Your Personal Information will only be accessed internally on a need-to-know basis, and it will only be accessed or processed if absolutely necessary.  We will constantly monitor and delete data that is no longer required by any relevant law or jurisdiction in which we operate.
                </Typography>
                <Typography
            sx={{
                textDecoration: 'underline',
                textDecorationColor: 'black', 
                fontFamily: 'Satoshi, sans-serif',
                color: '#FF6B00',
                fontWeight: 700,
                mt:2,
                fontSize: {
                xs: '16px',
                sm: '18px',
                md: '20px',
                },
                lineHeight: 1.5,
            }}
            >
              8 Cookies
               </Typography>
               <Typography
                sx={{
                    fontFamily: 'Satoshi, sans-serif',
                    fontSize: {
                    xs: '16px',  // small screens
                    sm: '18px',
                    md: '20px',  // medium and up
                    },
                    lineHeight: 1.5,
                    mt:1
                    
                }}
      
      >
     When you use PlatinX Exchange services, we may make use of the standard practice of placing tiny data files called cookies, flash cookies, pixel tags, or other tracking tools (hereinafter referred to as "Cookies") on your computer or other devices used to engage our services.  We use Cookies to (i) help us recognize you as a customer, collect information about your use of PlatinX Exchange services to better customize our services and content for you, and to collect information about your computer or other access devices as part to ensure our compliance with regulatory obligations and to (ii) ensure that your account security has not been compromised by detecting irregular or suspicious account activities.  We use both session and persistent Cookies.  Session Cookies expire when you log out of your User Account or close your browser.  Persistent Cookies remain on your computer or mobile device until deleted or otherwise expire.  Most web browsers are set to accept Cookies by default.  You are free to decline most of our session Cookies for each browser or browser add-on permit on each device you use to surf our website, but choosing to remove or disable our Cookies may interfere with your use and functionality of PlatinX Exchange services.
             </Typography>
             <Typography
            sx={{
                textDecoration: 'underline',
                textDecorationColor: 'black', 
                fontFamily: 'Satoshi, sans-serif',
                color: '#FF6B00',
                fontWeight: 700,
                mt:2,
                fontSize: {
                xs: '16px',
                sm: '18px',
                md: '20px',
                },
                lineHeight: 1.5,
            }}
            >
             9. Modification/Changes in Privacy Policy
               </Typography>

               <Typography
                sx={{
                    fontFamily: 'Satoshi, sans-serif',
                    fontSize: {
                    xs: '16px',  // small screens
                    sm: '18px',
                    md: '20px',  // medium and up
                    },
                    lineHeight: 1.5,
                    mt:1
                    
                }}
      
      >
At sole discretion of the Company, we may amend this Privacy Policy from time to time by posting the amended version on our website, publishing the effective date of the amended version, and highlighting amendments made.  We may announce such amendments to you via email, but you are strongly encouraged to review such Policy regularly.  If you do not agree with any of the amendments made, you shall stop using PlatinX Exchange services immediately.  Should you continue to use our services after we release an amended version of this Policy, your continued visit to PlatinX Exchange website shall indicate and show that you agree to the update and agree to comply with the updated Privacy Policy.

  </Typography>
  <Typography
            sx={{
                textDecoration: 'underline',
                textDecorationColor: 'black', 
                fontFamily: 'Satoshi, sans-serif',
                color: '#FF6B00',
                fontWeight: 700,
                mt:2,
                fontSize: {
                xs: '16px',
                sm: '18px',
                md: '20px',
                },
                lineHeight: 1.5,
            }}
            >
             10. Contact Information
               </Typography>

               <Typography
  sx={{
    fontFamily: 'Satoshi, sans-serif',
    fontWeight: 400,
    fontSize: {
      xs: '16px',
      sm: '18px',
      md: '20px',
    },
    lineHeight: 1.7,
    color: 'black',
    mt: 3,
    textAlign: 'justify',
  }}
>
  The data controller responsible for your personal data is PlatinX Exchange.
  Please write to us at{' '}
  <span
    style={{
      color: '#FF6B00',
      textDecoration: 'underline',
      textDecorationColor: '#FF6B00',
    }}
  >
    support@platinx.exchange
  </span>
</Typography>
     </Box>
        </Box>
    );
}; 
export default PrivacyPolicy